import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import "chartjs-adapter-date-fns";
import { Range } from "react-range";
import { fetchPostedProducts } from "../../services/InteractionAndOffer";
import Spinner from "../shared/Spinner";

// register Chart.js + plugins
Chart.register(...registerables, ChartDataLabels);

function TotalPostForUserPost() {
  const [viewMode, setViewMode] = useState("daily"); // "daily" | "monthly"

  const [dailyDates, setDailyDates] = useState([]);
  const [dailyCounts, setDailyCounts] = useState([]);
  const [dailyRange, setDailyRange] = useState([0, 0]);

  const [monthlyDates, setMonthlyDates] = useState([]);
  const [monthlyCounts, setMonthlyCounts] = useState([]);
  const [monthlyRange, setMonthlyRange] = useState([0, 0]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 1️⃣ fetch & compute both daily & monthly tallies
  useEffect(() => {
    async function fetchAndCompute() {
      try {
        const countsObj = await fetchPostedProducts();
        // Flatten and filter to "user" posters
        const allPosts = Object.values(countsObj).flatMap((c) => c.total);
        const userOnly = allPosts.filter(
          (p) => p.poster?.toLowerCase() === "user"
        );

        // DAILY tally
        const dayTally = {};
        userOnly.forEach((p) => {
          const day = new Date(p.createdAt).toISOString().split("T")[0];
          dayTally[day] = (dayTally[day] || 0) + 1;
        });
        const days = Object.keys(dayTally).sort(
          (a, b) => new Date(a) - new Date(b)
        );
        const dCounts = days.map((d) => dayTally[d]);

        setDailyDates(days);
        setDailyCounts(dCounts);
        if (days.length > 1) setDailyRange([0, days.length - 1]);

        // MONTHLY tally
        const monthTally = {};
        userOnly.forEach((p) => {
          const dt = new Date(p.createdAt);
          const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(
            2,
            "0"
          )}`;
          monthTally[key] = (monthTally[key] || 0) + 1;
        });
        const months = Object.keys(monthTally).sort(
          (a, b) => new Date(a + "-01") - new Date(b + "-01")
        );
        const mCounts = months.map((m) => monthTally[m]);

        setMonthlyDates(months);
        setMonthlyCounts(mCounts);
        if (months.length > 1) setMonthlyRange([0, months.length - 1]);
      } catch (err) {
        console.error(err);
        setError("Failed to load user-posted products");
      } finally {
        setLoading(false);
      }
    }

    fetchAndCompute();
  }, []);

  if (loading) {
    return (
      <div className="relative min-h-[calc(100vh-8rem)]">
        <Spinner size="xl" fullScreen={true} />
      </div>
    );
  }
  if (error) return <p className="text-red-500 text-center p-4">{error}</p>;

  // 2️⃣ pick the right series + range
  const [startIdx, endIdx] = viewMode === "daily" ? dailyRange : monthlyRange;

  const labels = (viewMode === "daily" ? dailyDates : monthlyDates).slice(
    startIdx,
    endIdx + 1
  );
  const data = (viewMode === "daily" ? dailyCounts : monthlyCounts).slice(
    startIdx,
    endIdx + 1
  );

  // 3️⃣ build chart.js data & options dynamically
  const chartData = {
    labels,
    datasets: [
      {
        label:
          viewMode === "daily"
            ? "User-posted Products per Day"
            : "User-posted Products per Month",
        data,
        fill: false,
        borderColor: "green",
        tension: 0.4,
        pointBackgroundColor: "green",
        datalabels: {
          anchor: "end",
          align: "top",
          font: { weight: "bold" },
          offset: 4,
        },
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: "time",
        time: {
          unit: viewMode === "daily" ? "day" : "month",
          tooltipFormat: viewMode === "daily" ? "MMMM d, yyyy" : "MMMM yyyy",
          displayFormats: {
            day: "MMM d, yyyy",
            month: "MMM yyyy",
          },
        },
        ticks: { autoSkip: true, maxRotation: 45, minRotation: 0 },
      },
      y: { beginAtZero: true },
    },
    plugins: {
      datalabels: { display: true },
    },
  };

  return (
    <div className="p-6 bg-white shadow-lg">
      <h2 className="mb-4">Products Posted by Users</h2>

      {/* VIEW DROPDOWN */}
      <div className="mb-4">
        <label className="font-medium">
          View:&nbsp;
          <select
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            className="border rounded px-2 py-1"
          >
            <option value="daily">Daily View</option>
            <option value="monthly">Monthly View</option>
          </select>
        </label>
      </div>

      {/* CHART */}
      <div style={{ height: "300px", position: "relative" }}>
        <Line data={chartData} options={options} />
      </div>

      {/* SLIDER */}
      {(viewMode === "daily" ? dailyDates.length : monthlyDates.length) > 1 && (
        <div className="mt-6">
          <Range
            values={viewMode === "daily" ? dailyRange : monthlyRange}
            step={1}
            min={0}
            max={
              (viewMode === "daily" ? dailyDates.length : monthlyDates.length) -
              1
            }
            onChange={(vals) =>
              viewMode === "daily" ? setDailyRange(vals) : setMonthlyRange(vals)
            }
            renderTrack={({ props, children }) => (
              <div
                {...props}
                style={{
                  ...props.style,
                  height: "8px",
                  width: "100%",
                  backgroundColor: "#ddd",
                  borderRadius: "8px",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    height: "100%",
                    backgroundColor: "#4A90E2",
                    borderRadius: "8px",
                    left: `${
                      (startIdx /
                        ((viewMode === "daily"
                          ? dailyDates.length
                          : monthlyDates.length) -
                          1)) *
                      100
                    }%`,
                    width: `${
                      ((endIdx - startIdx) /
                        ((viewMode === "daily"
                          ? dailyDates.length
                          : monthlyDates.length) -
                          1)) *
                      100
                    }%`,
                  }}
                />
                {children}
              </div>
            )}
            renderThumb={({ props, index }) => (
              <div
                {...props}
                style={{
                  ...props.style,
                  height: "20px",
                  width: "20px",
                  backgroundColor: "#4A90E2",
                  borderRadius: "4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: "-28px",
                    fontSize: "12px",
                    color: "#333",
                  }}
                >
                  {
                    (viewMode === "daily" ? dailyDates : monthlyDates)[
                      viewMode === "daily"
                        ? dailyRange[index]
                        : monthlyRange[index]
                    ]
                  }
                </div>
              </div>
            )}
          />

          <div className="mt-2 font-medium">
            Showing from <strong>{labels[0]}</strong> to{" "}
            <strong>{labels[labels.length - 1]}</strong>
          </div>
        </div>
      )}
    </div>
  );
}

export default TotalPostForUserPost;
